import { useMemo, useState } from 'react';
import type { Cotacao } from '../../types/agent';
import { formatPrice } from '../../lib/format';
import { FlowSheet } from './FlowSheet';

/** Um plano (30/60/90 dias) dentro de dados.cotacoes — shape do motor unified-pricer. */
export type Plano = {
  dias: number;
  preco: number;
  por_dia?: number;
  n_unidades?: number;
  desconto_pct?: number;
};

export type Ingrediente = { nome?: string; dose?: number | string; unidade?: string };

/** Item cru do motor: pode vir como {nome,dose,unidade} (extração) OU {texto,dose_mg,tpcmp} (resolvido). */
type IngredienteBruto = {
  nome?: string;
  texto?: string;
  dose?: number | string;
  dose_mg?: number;
  unidade?: string;
  tpcmp?: string;
};

/** Extrai (defensivamente) os campos ricos do payload opaco `dados` do motor. */
export function parseCotacao(cotacao: Cotacao) {
  const d = cotacao.dados ?? {};
  const planos = (Array.isArray(d.cotacoes) ? d.cotacoes : []) as Plano[];
  const brutos = (Array.isArray(d.ingredientes) ? d.ingredientes : []) as IngredienteBruto[];
  const ingredientes: Ingrediente[] = brutos
    .filter((i) => i && i.tpcmp !== 'S') // 'S' = base/veículo qsp — não listar como ativo
    .map((i) => ({
      nome: i.nome ?? i.texto,
      dose: i.dose ?? i.dose_mg,
      unidade: i.unidade ?? (i.dose_mg !== undefined && i.dose === undefined ? 'mg' : undefined),
    }))
    .filter((i) => i.nome);
  return {
    ok: d.ok !== false,
    planos: planos.filter((p) => typeof p?.dias === 'number' && typeof p?.preco === 'number'),
    ingredientes,
    posologia: typeof d.posologia === 'string' ? d.posologia : undefined,
    forma: typeof d.forma === 'string' ? d.forma : undefined,
    economiaPct: typeof d.economia_pct === 'number' ? d.economia_pct : undefined,
    precoRecomendado:
      typeof d.preco_recomendado === 'number'
        ? d.preco_recomendado
        : typeof cotacao.preco === 'number'
          ? cotacao.preco
          : undefined,
  };
}

/**
 * Combina N cotações (receita com múltiplas fórmulas) num único resumo: soma o preço de
 * cada plano (30/60/90 dias) através de TODAS as fórmulas, concatena os ingredientes, e
 * junta posologia/forma de cada fórmula numa lista legível. Achado real 2026-07-06: sem
 * isso, só a ÚLTIMA fórmula aparecia no card/checkout — o cliente via 1 preço, não a soma.
 */
export function combinarCotacoes(cotacoes: Cotacao[]) {
  const parsed = cotacoes.map((c) => parseCotacao(c));

  const porDias = new Map<number, { preco: number; nUnidades?: number }>();
  for (const p of parsed) {
    for (const plano of p.planos) {
      const atual = porDias.get(plano.dias) ?? { preco: 0, nUnidades: 0 };
      porDias.set(plano.dias, {
        preco: atual.preco + plano.preco,
        nUnidades: (atual.nUnidades ?? 0) + (plano.n_unidades ?? 0),
      });
    }
  }
  const planosCombinados: Plano[] = Array.from(porDias.entries())
    .map(([dias, v]) => ({ dias, preco: Math.round(v.preco * 100) / 100, n_unidades: v.nUnidades }))
    .sort((a, b) => a.dias - b.dias);

  // economia % combinada: relativa ao preço de 30 dias × (dias/30), igual à lógica de plano único.
  const base30 = planosCombinados.find((p) => p.dias === 30)?.preco;
  for (const p of planosCombinados) {
    if (base30 && p.dias !== 30) {
      const esperado = base30 * (p.dias / 30);
      p.desconto_pct = esperado > 0 ? Math.round(100 * (1 - p.preco / esperado)) : 0;
    }
  }

  const ingredientesCombinados = parsed.flatMap((p, idx) =>
    p.ingredientes.map((i) => ({ ...i, formulaIndex: idx + 1 }))
  );
  const posologiasCombinadas = parsed
    .map((p, idx) => (p.posologia ? `Fórmula ${idx + 1}: ${p.posologia}` : null))
    .filter(Boolean)
    .join(' · ');
  const formasCombinadas = Array.from(new Set(parsed.map((p) => p.forma).filter(Boolean)));

  const p90 = planosCombinados.find((p) => p.dias === 90);
  return {
    ok: parsed.every((p) => p.ok),
    planos: planosCombinados,
    ingredientes: ingredientesCombinados,
    posologia: posologiasCombinadas || undefined,
    forma: formasCombinadas.length === 1 ? formasCombinadas[0] : formasCombinadas.join(' + '),
    economiaPct: p90?.desconto_pct,
    precoRecomendado: planosCombinados.find((p) => p.dias === 30)?.preco,
    nFormulas: parsed.length,
  };
}

/** Ilustração do "produto" (frasco magistral + cápsulas) — header do cartão, estilo pedido do WhatsApp. */
function ProdutoArt() {
  return (
    <svg viewBox="0 0 320 96" className="w-full h-[88px] block" role="img" aria-label="Fórmula manipulada">
      <defs>
        <linearGradient id="cotcard-bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#DCF8EF" />
          <stop offset="1" stopColor="#C2EFE0" />
        </linearGradient>
      </defs>
      <rect width="320" height="96" fill="url(#cotcard-bg)" />
      {/* bolhas decorativas */}
      <circle cx="288" cy="14" r="26" fill="#B5EBD9" opacity="0.6" />
      <circle cx="30" cy="84" r="20" fill="#B5EBD9" opacity="0.5" />
      {/* frasco */}
      <g transform="translate(128,14)">
        <rect x="10" y="0" width="24" height="10" rx="2.5" fill="#075E54" />
        <rect x="6" y="10" width="32" height="58" rx="9" fill="#FFFFFF" stroke="#075E54" strokeWidth="2.5" />
        <rect x="11" y="26" width="22" height="24" rx="3" fill="#25D366" opacity="0.25" />
        <rect x="11" y="26" width="22" height="24" rx="3" fill="none" stroke="#128C7E" strokeWidth="1.4" />
        <line x1="14" y1="33" x2="30" y2="33" stroke="#128C7E" strokeWidth="1.6" strokeLinecap="round" />
        <line x1="14" y1="38" x2="26" y2="38" stroke="#128C7E" strokeWidth="1.4" strokeLinecap="round" opacity="0.7" />
      </g>
      {/* cápsulas soltas */}
      <g transform="translate(190,54) rotate(24)">
        <rect x="0" y="0" width="34" height="15" rx="7.5" fill="#075E54" />
        <rect x="0" y="0" width="17" height="15" rx="7.5" fill="#25D366" />
        <line x1="17" y1="1.5" x2="17" y2="13.5" stroke="#FFFFFF" strokeWidth="1.6" />
      </g>
      <g transform="translate(86,58) rotate(-18)">
        <rect x="0" y="0" width="28" height="12" rx="6" fill="#128C7E" />
        <rect x="0" y="0" width="14" height="12" rx="6" fill="#FFFFFF" stroke="#128C7E" strokeWidth="1.4" />
      </g>
    </svg>
  );
}

/**
 * Cartão de cotação no estilo NATIVO "mensagem de pedido" do WhatsApp:
 * imagem do produto no topo, preço em destaque, planos tocáveis (30/60/90),
 * e CTA "Fechar pedido" que abre o checkout em WhatsApp Flow (bottom sheet).
 */
export function CotacaoCard({ cotacao, cotacoes }: { cotacao: Cotacao; cotacoes?: Cotacao[] }) {
  // Múltiplas fórmulas (2+ cotações no mesmo turno) -> soma os planos numa cotação
  // combinada; 1 fórmula (o caso de sempre) -> comportamento IDÊNTICO ao de antes.
  const multi = (cotacoes?.length ?? 0) > 1;
  const parsed = useMemo(
    () => (multi ? combinarCotacoes(cotacoes as Cotacao[]) : parseCotacao(cotacao)),
    [cotacao, cotacoes, multi]
  );
  const [sheetPlano, setSheetPlano] = useState<number | null>(null); // dias pré-selecionado
  const { planos, ingredientes, precoRecomendado, economiaPct } = parsed;
  const nFormulas = multi ? (parsed as ReturnType<typeof combinarCotacoes>).nFormulas : undefined;

  if (!planos.length && precoRecomendado === undefined) return null;

  const nAtivos = ingredientes.filter((i) => i?.nome).length;
  const melhor = planos.find((p) => p.dias === 90) ?? planos.at(-1);

  return (
    <div
      data-testid="cotacao-card"
      className="mt-1.5 -mx-0.5 rounded-[10px] overflow-hidden border border-[#E6E9EC] bg-white"
    >
      <ProdutoArt />

      <div className="px-3 pt-2 pb-1.5">
        <div className="text-[10.5px] font-semibold tracking-[0.08em] uppercase text-[#128C7E]">
          {nFormulas ? `Orçamento total · ${nFormulas} fórmulas` : 'Orçamento personalizado'}
        </div>
        {precoRecomendado !== undefined && (
          <div className="flex items-baseline gap-1.5">
            <span className="text-[20px] font-semibold text-[#075E54]">
              {formatPrice(precoRecomendado)}
            </span>
            <span className="text-[12px] text-[#667781]">/ 30 dias</span>
          </div>
        )}
        {nAtivos > 0 && (
          <div className="text-[12px] text-[#667781] mt-0.5">
            {nAtivos} {nAtivos === 1 ? 'ativo' : 'ativos'} manipulados
            {nFormulas ? ` em ${nFormulas} fórmulas` : ' · fórmula exclusiva'}
          </div>
        )}
      </div>

      {planos.length > 0 && (
        <div className="border-t border-[#F0F2F5]">
          {planos.map((p) => {
            const destaque = p.dias === 90;
            return (
              <button
                key={p.dias}
                type="button"
                onClick={() => setSheetPlano(p.dias)}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-left active:bg-[#F5F6F6] transition-colors border-b border-[#F0F2F5] last:border-b-0"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[13.5px] font-medium text-[#111B21]">{p.dias} dias</span>
                    {destaque && (
                      <span className="text-[9.5px] font-bold uppercase tracking-wide bg-[#25D366]/15 text-[#0B846B] rounded-full px-1.5 py-[1px]">
                        🏆 mais escolhido
                      </span>
                    )}
                  </div>
                  {p.n_unidades && p.n_unidades > 1 && (
                    <div className="text-[11.5px] text-[#8696A0]">{Math.round(p.n_unidades)} unidades</div>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-[14px] font-semibold text-[#075E54]">{formatPrice(p.preco)}</div>
                  {(p.desconto_pct ?? 0) > 0 && (
                    <div className="text-[11px] font-medium text-[#0B846B]">-{p.desconto_pct}%</div>
                  )}
                </div>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#8696A0" aria-hidden="true">
                  <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                </svg>
              </button>
            );
          })}
        </div>
      )}

      {economiaPct !== undefined && economiaPct > 0 && (
        <div className="px-3 py-1.5 bg-[#F0FDF4] text-[11.5px] text-[#0B846B]">
          💚 No plano de 90 dias você economiza <b>{economiaPct}%</b> vs mês a mês
        </div>
      )}

      <div className="border-t border-[#F0F2F5] p-1.5">
        <button
          type="button"
          onClick={() => setSheetPlano(melhor?.dias ?? 30)}
          className="w-full flex items-center justify-center gap-1.5 rounded-[8px] py-[9px] text-[14px] font-semibold text-white bg-[#1DAB61] active:bg-[#179552] transition-colors"
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z" />
          </svg>
          Fechar pedido
        </button>
      </div>

      {sheetPlano !== null && (
        <FlowSheet cotacao={parsed} planoInicial={sheetPlano} onClose={() => setSheetPlano(null)} />
      )}
    </div>
  );
}
